/**
 * AddressAutocomplete - address entry with Google Places suggestion.
 *
 * Suggestions show the full address but only the street is kept in the field;
 * city, state, and ZIP are reported to the parent. The field itself is owned by
 * the Places seam, which is why this component renders a host rather than an
 * input. See TCA-26092103.
 */
import React, { useEffect, useRef } from 'react';
import { MapPin } from 'lucide-react';
import { INPUT_CLASS_NAME } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { ADDRESS_SELECTION, mountPlacesField } from '@/utils/googlePlacesAutocomplete';

const AddressAutocomplete = ({
  value,
  onChange,
  onAddressSelect,
  placeholder = "Start typing an address...",
  disabled = false,
  className = ''
}) => {
  const hostRef = useRef(null);
  const fieldRef = useRef(null);

  // Stable refs to avoid stale closures
  const onAddressSelectRef = useRef(onAddressSelect);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onAddressSelectRef.current = onAddressSelect;
    onChangeRef.current = onChange;
  }, [onAddressSelect, onChange]);

  // The field is mounted once. Later prop changes are pushed through the
  // handle rather than remounting, so typing is never interrupted.
  useEffect(() => {
    const field = mountPlacesField(hostRef.current, {
      kind: ADDRESS_SELECTION,
      value,
      placeholder,
      disabled,
      className: cn(INPUT_CLASS_NAME, className, 'pl-10'),
      testId: 'address-autocomplete-input',
      onChange: (text) => onChangeRef.current?.(text),
      onSelect: (address) => onAddressSelectRef.current?.(address),
    });
    fieldRef.current = field;
    return () => {
      field?.destroy();
      fieldRef.current = null;
    };
    // Mount once; the setters below carry every later change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fieldRef.current?.setValue(value); }, [value]);
  useEffect(() => { fieldRef.current?.setDisabled(disabled); }, [disabled]);
  useEffect(() => { fieldRef.current?.setPlaceholder(placeholder); }, [placeholder]);

  return (
    <div className="relative" ref={hostRef}>
      <MapPin className="absolute left-3 top-[22px] -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none z-10" />
    </div>
  );
};

export default AddressAutocomplete;
